import { TypeManagerDecorator } from "../../main/type.map.service";
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    input,
    OnInit,
    ViewEncapsulation,
    signal,
    effect,
    inject,
} from "@angular/core";
import { S25DateParse } from "../../util/core/s25.date.parse";
import { S25Util } from "../../util/s25-util";
import { IngestionFieldAnswer, IngestionSubmission, DataIngestionService } from "./data.ingestion.service";
import { S25Datefilter } from "../s25-dateformat/s25.datefilter.service";
import { UserprefService } from "../../services/userpref.service";
import { AlertService } from "../../services/alert.service";
import { NotificationService } from "../../services/notification.service";
import { S25Const } from "../../util/s25-const";
import { resolveSuccessMessagePlainText } from "./field-types/ingestion.form.success-message";
import { Workflow } from "../workflow/service/Workflow";
import { WorkflowService } from "../workflow/service/workflow.service";
import { IngestionPointCategory } from "./data.ingestion.const";
import { WorkflowProgressDiagramStore } from "../workflow/workflow.progress.diagram.store";
import { WorkflowChatController } from "../workflow/chat/workflow.chat.controller";
import { DiagramChatRequest } from "../workflow/diagram/workflow.diagram.model";
import {
    isSelfSavedDraft,
    isWorkflowSpawnedDraft,
    submissionMatchesDashboardStatusFilter,
} from "./ingestion.dashboard.util";

@TypeManagerDecorator("s25-ng-ingestion-dashboard")
@Component({
    selector: "s25-ng-ingestion-dashboard",
    template: `
        @if (init) {
            <!-- Dashboard View -->
            <div class="c-margin-top--single" [hidden]="!showDashboard()">
                @if (showFormsAvailableForms()) {
                    <s25-simple-collapse
                        class="available-forms-section"
                        [header]="'Available Forms'"
                        [expanded]="availableFormsExpanded()"
                        (expandedChange)="availableFormsExpanded.set($event)"
                    >
                        <s25-ng-fillable-forms
                            [category]="selectedCategory()"
                            (startForm)="startNewForm($event)"
                            class="c-margin-left--single c-margin-right--single c-margin-bottom--single"
                        ></s25-ng-fillable-forms>
                    </s25-simple-collapse>
                } @else {
                    <s25-ng-button
                        class="c-margin-bottom--single"
                        [type]="'outline'"
                        [onClick]="showAvailableForms"
                        [buttonClass]="'refresh-button'"
                    >
                        Start a new form
                    </s25-ng-button>
                }

                <!-- Always show refresh button -->
                <div class="section-header">
                    <h3>Form Submissions</h3>
                    <s25-ng-button [type]="'outline'" [onClick]="refresh" [buttonClass]="'refresh-button'">
                        <i class="fa fa-refresh"></i> Refresh
                    </s25-ng-button>
                </div>

                <div class="filters-container">
                    <div class="filter-group">
                        <label class="filter-label" for="ingestion-dashboard-form-filter">Form</label>
                        <select
                            id="ingestion-dashboard-form-filter"
                            class="filter-control"
                            [value]="filterFormType() ?? ''"
                            (change)="onFormTypeFilterChange($event)"
                        >
                            <option value="">All forms</option>
                            @for (option of ingestionPointFilterOptions; track option.ingestionPointId) {
                                <option [value]="option.ingestionPointId">{{ option.label }}</option>
                            }
                        </select>
                    </div>
                    <div class="filter-group filter-date-from">
                        <label class="filter-label" for="ingestion-dashboard-date-from">From</label>
                        <input
                            id="ingestion-dashboard-date-from"
                            class="filter-control"
                            type="date"
                            [value]="filterDateFrom() ?? ''"
                            (change)="onFilterDateFromChange($event)"
                        />
                    </div>
                    <div class="filter-group filter-date-to">
                        <label class="filter-label" for="ingestion-dashboard-date-to">To</label>
                        <input
                            id="ingestion-dashboard-date-to"
                            class="filter-control"
                            type="date"
                            [value]="filterDateTo() ?? ''"
                            (change)="onFilterDateToChange($event)"
                        />
                    </div>
                    <div class="filter-group">
                        <label class="filter-label" for="ingestion-dashboard-status-filter">Status</label>
                        <select
                            id="ingestion-dashboard-status-filter"
                            class="filter-control"
                            [value]="filterStatus() ?? ''"
                            (change)="onFilterStatusChange($event)"
                        >
                            <option value="">All statuses</option>
                            <option value="DRAFT">Draft (all)</option>
                            <option value="ACTION_NEEDED">Action needed</option>
                            <option value="SAVED_DRAFT">Saved draft</option>
                            <option value="SUBMITTED">Submitted (no workflow)</option>
                            <option value="PENDING">Pending</option>
                            <option value="IN_PROGRESS">In progress</option>
                            <option value="REQUESTED_CHANGES">Changes requested</option>
                            <option value="COMPLETED">Completed</option>
                            <option value="DENIED">Denied</option>
                            <option value="CANCELED">Canceled</option>
                        </select>
                    </div>
                    <div class="filter-group filter-actions">
                        <s25-ng-button
                            [type]="'outline'"
                            [onClick]="clearFilters"
                            [buttonClass]="'filter-clear-button'"
                        >
                            Clear filters
                        </s25-ng-button>
                    </div>
                </div>

                @if (
                    hasActiveFilters() &&
                    !filteredActionNeededSubmissions?.length &&
                    !filteredSpawnedActionSubmissions?.length &&
                    !filteredSavedDraftSubmissions?.length &&
                    !filteredSubmittedSubmissions?.length
                ) {
                    <div class="empty-state">
                        <h3>No submissions match</h3>
                        <p>No submissions match your filter criteria. Try adjusting or clearing filters.</p>
                    </div>
                }

                @if (filteredActionNeededSubmissions?.length) {
                    <div class="section">
                        <h3 class="c-margin-bottom--single">Returned Forms</h3>
                        <div class="submissions-list">
                            @for (submission of filteredActionNeededSubmissions; track submission.submissionIdUuid) {
                                <div class="submission-card action-needed">
                                    <div class="submission-header">
                                        <h4>
                                            {{ submission.ingestionPoint?.ingestionPointName || "Form Submission" }}
                                        </h4>
                                        <div class="submission-status-group">
                                            @if (submission.workflowStatus === "REQUESTED_CHANGES") {
                                                <span class="submission-status requested-changes-status">
                                                    Changes Requested
                                                </span>
                                            }
                                        </div>
                                    </div>
                                    <p class="submission-meta">
                                        Last modified:
                                        {{ formattedDatesBySubmissionId.get(submission.submissionIdUuid) ?? "" }}
                                    </p>
                                    @if (submission.workflowStatus === "REQUESTED_CHANGES") {
                                        <s25-ng-requested-changes
                                            [itemTypeId]="WORKFLOW_INGESTION_SUBMISSION_OBJECT_TYPE"
                                            [itemId]="submission.submissionId"
                                        ></s25-ng-requested-changes>
                                    }
                                    <div class="submission-actions">
                                        @if (submission.submitted) {
                                            <s25-ng-button
                                                [type]="'outline'"
                                                [onClick]="viewSubmission"
                                                [onClickArgs]="[submission.submissionIdUuid]"
                                                [buttonClass]="'c-margin-right--single'"
                                            >
                                                View
                                            </s25-ng-button>
                                        } @else {
                                            <s25-ng-button
                                                [type]="'primary'"
                                                [onClick]="continueForm"
                                                [onClickArgs]="[submission.submissionIdUuid]"
                                                [buttonClass]="'c-margin-right--single'"
                                            >
                                                Continue
                                            </s25-ng-button>
                                            <s25-ng-button
                                                [type]="'danger--outline'"
                                                [onClick]="deleteSubmission"
                                                [onClickArgs]="[submission.submissionIdUuid]"
                                            >
                                                Delete
                                            </s25-ng-button>
                                        }
                                        <ng-container
                                            *ngTemplateOutlet="diagramButton; context: { $implicit: submission }"
                                        ></ng-container>
                                    </div>
                                </div>
                            }
                        </div>
                    </div>
                }
                @if (filteredSpawnedActionSubmissions?.length) {
                    <div class="section">
                        <h3 class="c-margin-bottom--single section-heading--action-needed">Action Needed</h3>
                        <div class="submissions-list">
                            @for (submission of filteredSpawnedActionSubmissions; track submission.submissionIdUuid) {
                                <div class="submission-card spawned-action-needed">
                                    <div class="submission-header">
                                        <h4>
                                            {{ submission.ingestionPoint?.ingestionPointName || "Form Submission" }}
                                        </h4>
                                        <div class="submission-status-group">
                                            <span class="submission-status action-needed-status">Action needed</span>
                                        </div>
                                    </div>
                                    <p class="submission-meta">Assigned by workflow</p>
                                    <p class="submission-meta">
                                        Last modified:
                                        {{ formattedDatesBySubmissionId.get(submission.submissionIdUuid) ?? "" }}
                                    </p>
                                    <div class="submission-actions">
                                        <s25-ng-button
                                            [type]="'primary'"
                                            [onClick]="continueForm"
                                            [onClickArgs]="[submission.submissionIdUuid]"
                                            [buttonClass]="'c-margin-right--single'"
                                        >
                                            Continue
                                        </s25-ng-button>
                                        <s25-ng-button
                                            [type]="'danger--outline'"
                                            [onClick]="deleteSubmission"
                                            [onClickArgs]="[submission.submissionIdUuid]"
                                        >
                                            Delete
                                        </s25-ng-button>
                                        <ng-container
                                            *ngTemplateOutlet="diagramButton; context: { $implicit: submission }"
                                        ></ng-container>
                                    </div>
                                </div>
                            }
                        </div>
                    </div>
                }
                @if (filteredSavedDraftSubmissions?.length) {
                    <div class="submissions-section">
                        <h3 class="c-margin-bottom--single">Saved Drafts</h3>
                        <div class="submissions-list">
                            @for (submission of filteredSavedDraftSubmissions; track submission.submissionIdUuid) {
                                <div class="submission-card in-progress">
                                    <div class="submission-header">
                                        <h4>
                                            {{ submission.ingestionPoint?.ingestionPointName || "Form Submission" }}
                                        </h4>
                                        <div class="submission-status-group">
                                            <span
                                                class="submission-status"
                                                [class.requested-changes-status]="
                                                    submission.workflowStatus === 'REQUESTED_CHANGES'
                                                "
                                            >
                                                {{
                                                    submission.workflowStatus === "REQUESTED_CHANGES"
                                                        ? "Changes Requested"
                                                        : "Draft"
                                                }}
                                            </span>
                                        </div>
                                    </div>
                                    <p class="submission-meta">
                                        Last modified:
                                        {{ formattedDatesBySubmissionId.get(submission.submissionIdUuid) ?? "" }}
                                    </p>
                                    @if (submission.workflowStatus === "REQUESTED_CHANGES") {
                                        <s25-ng-requested-changes
                                            [itemTypeId]="WORKFLOW_INGESTION_SUBMISSION_OBJECT_TYPE"
                                            [itemId]="submission.submissionId"
                                        ></s25-ng-requested-changes>
                                    }
                                    <div class="submission-actions">
                                        @if (submission.submitted) {
                                            <s25-ng-button
                                                [type]="'outline'"
                                                [onClick]="viewSubmission"
                                                [onClickArgs]="[submission.submissionIdUuid]"
                                                [buttonClass]="'c-margin-right--single'"
                                            >
                                                View
                                            </s25-ng-button>
                                        } @else {
                                            <s25-ng-button
                                                [type]="'primary'"
                                                [onClick]="continueForm"
                                                [onClickArgs]="[submission.submissionIdUuid]"
                                                [buttonClass]="'c-margin-right--single'"
                                            >
                                                Continue
                                            </s25-ng-button>
                                            <s25-ng-button
                                                [type]="'danger--outline'"
                                                [onClick]="deleteSubmission"
                                                [onClickArgs]="[submission.submissionIdUuid]"
                                            >
                                                Delete
                                            </s25-ng-button>
                                        }
                                        <ng-container
                                            *ngTemplateOutlet="diagramButton; context: { $implicit: submission }"
                                        ></ng-container>
                                    </div>
                                </div>
                            }
                        </div>
                    </div>
                }

                @if (filteredSubmittedSubmissions?.length) {
                    <div class="section">
                        <h3 class="c-margin-bottom--single">Submitted Forms</h3>
                        <div class="submissions-list">
                            @for (submission of filteredSubmittedSubmissions; track submission.submissionIdUuid) {
                                <div class="submission-card submitted">
                                    <div class="submission-header">
                                        <h4>{{ submission.ingestionPoint?.ingestionPointName || "Submitted Form" }}</h4>
                                        <div class="submission-status-group">
                                            @if (!submission.workflowStatus) {
                                                <span class="submission-status"> Submitted </span>
                                            }
                                            @if (submission.workflowStatus) {
                                                <span
                                                    class="workflow-status-badge"
                                                    [class.status-pending]="submission.workflowStatus === 'PENDING'"
                                                    [class.status-in-progress]="
                                                        submission.workflowStatus === 'IN_PROGRESS'
                                                    "
                                                    [class.status-completed]="submission.workflowStatus === 'COMPLETED'"
                                                    [class.status-denied]="submission.workflowStatus === 'DENIED'"
                                                    [class.status-canceled]="submission.workflowStatus === 'CANCELED'"
                                                    >{{
                                                        submission.workflowStatus === "PENDING"
                                                            ? "Pending"
                                                            : submission.workflowStatus === "IN_PROGRESS"
                                                              ? "In Progress"
                                                              : submission.workflowStatus === "COMPLETED"
                                                                ? "Completed"
                                                                : submission.workflowStatus === "DENIED"
                                                                  ? "Denied"
                                                                  : submission.workflowStatus === "CANCELED"
                                                                    ? "Canceled"
                                                                    : submission.workflowStatus
                                                    }}</span
                                                >
                                            }
                                        </div>
                                    </div>
                                    <p class="submission-meta">
                                        Submitted:
                                        {{ formattedDatesBySubmissionId.get(submission.submissionIdUuid) ?? "" }}
                                    </p>
                                    <div class="submission-actions">
                                        <s25-ng-button
                                            [type]="'outline'"
                                            [onClick]="viewSubmission"
                                            [onClickArgs]="[submission.submissionIdUuid]"
                                        >
                                            View
                                        </s25-ng-button>
                                        <ng-container
                                            *ngTemplateOutlet="diagramButton; context: { $implicit: submission }"
                                        ></ng-container>
                                        @if (
                                            submission.submitted &&
                                            submission.ingestionPoint?.allowEditAfterSubmit &&
                                            (submission.workflowStatus === "PENDING" ||
                                                submission.workflowStatus === "IN_PROGRESS")
                                        ) {
                                            <s25-ng-button
                                                [type]="'outline'"
                                                [onClick]="editSubmission"
                                                [onClickArgs]="[submission.submissionIdUuid]"
                                            >
                                                Edit
                                            </s25-ng-button>
                                        }
                                        @if (
                                            submission.submitted &&
                                            (submission.workflowStatus === "IN_PROGRESS" ||
                                                submission.workflowStatus === "PENDING" ||
                                                submission.workflowStatus === "REQUESTED_CHANGES")
                                        ) {
                                            <s25-ng-button
                                                [type]="'danger--outline'"
                                                [onClick]="cancelWorkflow"
                                                [onClickArgs]="[submission]"
                                            >
                                                Cancel Workflow
                                            </s25-ng-button>
                                        }
                                    </div>
                                </div>
                            }
                        </div>
                    </div>
                }

                @if (
                    !hasActiveFilters() &&
                    !actionNeededSubmissions?.length &&
                    !spawnedActionSubmissions?.length &&
                    !savedDraftSubmissions?.length &&
                    !submittedSubmissions?.length
                ) {
                    <div class="empty-state">
                        <h3>No submissions yet</h3>
                        <p>
                            You do not have any form submissions yet. Use the refresh button above to check for updates.
                        </p>
                    </div>
                }

                @if (diagramStore.showDiagram()) {
                    <div draggableElement [fitToViewport]="true" class="workflow-diagram-panel">
                        <workflow-diagram-viewer
                            [workflowProcess]="diagramStore.diagramProcess()"
                            [showProgress]="true"
                            [chatAvailable]="!!diagramStore.submission()"
                            [showDiagram]="diagramStore.showDiagram()"
                            (showDiagramChange)="diagramStore.hide()"
                            (chatWith)="openDiagramChat($event)"
                        ></workflow-diagram-viewer>
                    </div>
                }
                <workflow-chat-panel [controller]="diagramChat" />
            </div>

            <!-- Form Outlet -->
            @if (!showDashboard() && currentIngestionPointId()) {
                <s25-ng-button [type]="'link'" [onClick]="onFormClose" class="back-dash">
                    &larr; Back to Forms Dash
                </s25-ng-button>
                <s25-ng-ingestion-form
                    [submissionIdUuid]="currentSubmissionIdUuid()"
                    [ingestionPointId]="currentIngestionPointId()"
                    [readOnly]="isReadOnlyView()"
                    [editMode]="isEditMode()"
                    [allowAnswerSeedToNewDraft]="true"
                    [showFormExitActions]="true"
                    (onSubmit)="onFormSubmit($event)"
                    (onClose)="onFormClose()"
                    (forkContextCreated)="onForkContextCreated($event)"
                ></s25-ng-ingestion-form>
            }
        }

        <ng-template #diagramButton let-submission>
            @if (submission.workflowProcessInstanceId) {
                <button class="aw-button aw-button--outline" (click)="toggleDiagram(submission)">
                    {{
                        diagramStore.showDiagram() &&
                        diagramStore.processInstanceId() === submission.workflowProcessInstanceId
                            ? "Hide"
                            : "Show"
                    }}
                    Workflow
                </button>
            }
        </ng-template>
    `,
    styles: `
        :host {
            --button--text-align: center;
        }

        .section {
            margin-top: 2rem;
            margin-bottom: 2rem;
        }

        .available-forms-section {
            display: block;
            margin-bottom: 2rem;
        }

        .available-forms-section ::ng-deep .content-wrapper {
            padding-top: 1rem;
        }

        .section-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
        }

        .section-header h3 {
            margin-bottom: 0;
        }

        .refresh-button {
            font-size: 0.9rem;
        }

        .filters-container {
            display: flex;
            gap: 1rem;
            flex-wrap: wrap;
            padding: 1rem;
            background-color: var(--color--surface-2-bg);
            border-radius: 4px;
            align-items: flex-end;
            margin-bottom: 1rem;
        }

        .filter-group {
            display: flex;
            flex-direction: column;
            gap: 0.25rem;
            min-width: 150px;
        }

        .filter-label {
            font-size: 0.875rem;
            font-weight: 500;
            color: var(--color--text);
            margin: 0;
        }

        .filter-control {
            padding: 0.5rem;
            border: 1px solid var(--color--border-bold);
            border-radius: 4px;
            font-size: 0.9rem;
            background-color: var(--color--surface-bg);
            color: var(--color--text);
        }

        .filter-control:focus {
            outline: none;
            border-color: var(--color--link-text);
            box-shadow: var(--shadow--form-focus);
        }

        .filter-actions {
            justify-content: flex-end;
        }

        .filter-actions .filter-clear-button {
            width: 100%;
        }

        .workflow-status-badge {
            font-weight: bold;
            padding: 0.25rem 0.5rem;
            border-radius: var(--border-radius);
            font-size: 0.9rem;
        }

        .workflow-status-badge.status-pending {
            background: color-mix(in srgb, var(--color--warning) 6%, var(--color--surface-bg));
            color: var(--color--warning);
        }

        .workflow-status-badge.status-in-progress {
            background: color-mix(in srgb, var(--cn-blue) 18%, var(--color--surface-bg));
            color: var(--color--link-text-hover);
        }

        .workflow-status-badge.status-completed {
            background: color-mix(in srgb, var(--color--text-success) 8%, var(--color--surface-bg));
            color: var(--color--text-success);
        }

        .workflow-status-badge.status-denied {
            background: color-mix(in srgb, var(--color--danger) 6%, var(--color--surface-bg));
            color: var(--color--text-danger);
        }

        .workflow-status-badge.status-canceled {
            background: var(--color--surface-2-bg);
            color: var(--color--text);
        }

        .refresh-button i {
            margin-right: 0.5rem;
        }

        .submissions-list {
            display: flex;
            flex-direction: column;
            gap: 1rem;
        }

        .submission-card {
            border: 1px solid var(--color--border);
            border-radius: 8px;
            padding: 1.5rem;
            background-color: var(--color--surface-bg);
        }

        .submission-card.in-progress {
            border-left: 4px solid var(--color--warning);
        }

        .submission-card.submitted {
            border-left: 4px solid var(--color--registration);
        }

        .submission-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 0.5rem;
        }

        .submission-header h4 {
            margin: 0;
            color: var(--color--link-text);
        }

        .submission-status {
            font-weight: bold;
            padding: 0.25rem 0.5rem;
            border-radius: 4px;
            font-size: 0.9rem;
        }

        .submission-card.in-progress .submission-status {
            background-color: var(--color--warning);
            color: var(--color--surface-bg);
        }

        .submission-card.submitted .submission-status {
            background-color: var(--color--registration);
            color: white;
        }

        .submission-status-group {
            display: flex;
            flex-direction: row;
            gap: 0.5rem;
            align-items: center;
            flex-wrap: wrap;
        }

        .section-heading--action-needed {
            color: var(--color--text-danger);
        }

        .submission-card.action-needed {
            border-left: 4px solid var(--color--warning);
        }

        .submission-card.spawned-action-needed {
            border-left: 4px solid var(--color--danger);
        }

        .submission-meta {
            margin: 0 0 1rem 0;
            color: var(--color--text-muted);
            font-size: 0.9rem;
        }

        .submission-actions {
            display: flex;
            gap: 0.5rem;
        }

        .requested-changes-status {
            background-color: #fff3e0 !important;
            color: #bf360c !important;
        }

        .spawned-action-needed .action-needed-status {
            background-color: var(--color--danger);
            color: white;
        }

        .empty-state {
            text-align: center;
            padding: 3rem;
            color: var(--color--text-muted);
        }

        .empty-state h3 {
            margin: 0 0 1rem 0;
        }

        .empty-state p {
            margin: 0;
        }

        .back-dash {
            float: right;
        }

        @media screen and (max-width: 768px) {
            .filters-container {
                display: grid;
                grid-template-columns: 1fr 1fr;
            }

            .filter-group {
                min-width: 0;
                grid-column: 1 / -1;
            }

            .filter-group.filter-date-from {
                grid-column: 1;
            }

            .filter-group.filter-date-to {
                grid-column: 2;
            }

            .filter-control {
                width: 100%;
                box-sizing: border-box;
            }
        }

        @media screen and (max-width: 480px) {
            .submission-card s25-ng-button {
                width: 100%;
            }
        }

        .workflow-diagram-panel {
            height: auto;
            display: flex;
            flex-direction: column;
            overflow: auto;
            background: var(--color--surface-2-bg);
            border: 1px solid var(--color--border);
            border-radius: 8px;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
            margin-top: 1rem;
            width: 95vw;
            max-width: 100%;
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class IngestionDashboardComponent implements OnInit {
    readonly cd = inject(ChangeDetectorRef);

    spawnedActionSubmissions: IngestionSubmission[];
    savedDraftSubmissions: IngestionSubmission[];
    submittedSubmissions: IngestionSubmission[];
    actionNeededSubmissions: IngestionSubmission[];
    init = false;
    dateTimeFormat = "MM/dd/yyyy h:mm a";
    formattedDatesBySubmissionId = new Map<string, string>();

    /** Distinct forms present in the current submission lists (for filter dropdown). */
    ingestionPointFilterOptions: { ingestionPointId: number; label: string }[] = [];

    filterFormType = signal<number | null>(null);
    filterDateFrom = signal<string | null>(null);
    filterDateTo = signal<string | null>(null);
    filterStatus = signal<string | null>(null);

    // Auto-refresh when component becomes visible
    activeMode = input<string | null>(null);
    selectedCategory = input(IngestionPointCategory.ENGAGEMENT);

    // Form outlet management
    availableFormsExpanded = signal(true);
    showDashboard = signal<boolean>(true);
    currentSubmissionIdUuid = signal<string | null>(null);
    currentIngestionPointId = signal<number | null>(null);
    isReadOnlyView = signal<boolean>(false);
    isEditMode = signal<boolean>(false);
    showFormsAvailableForms = signal<boolean>(false);

    protected readonly diagramStore = inject(WorkflowProgressDiagramStore);
    protected readonly diagramChat = new WorkflowChatController();

    constructor() {
        // Auto-refresh when component becomes visible (transitions from hidden to visible)
        let wasVisible = false;
        let hasInitialized = false;
        effect(() => {
            const isVisible = this.activeMode() === "myForms";
            if (isVisible && !wasVisible && hasInitialized) {
                // Component just became visible after being hidden - refresh
                this.refresh();
            }
            if (isVisible) {
                hasInitialized = true;
            }
            wasVisible = isVisible;
        });
    }

    ngOnInit() {
        this.refresh();
    }

    showAvailableForms = () => {
        this.showFormsAvailableForms.set(true);
    };

    refresh = async () => {
        try {
            this.init = false;
            this.cd.detectChanges();

            const [submissions, dateTimeFormat] = await Promise.all([
                DataIngestionService.getMySubmissions(),
                UserprefService.getS25DateTimeformat(),
            ]);

            this.dateTimeFormat = dateTimeFormat;

            // Precompute formatted dates once to avoid formatDate during change detection (script timeout)
            this.formattedDatesBySubmissionId.clear();
            for (const s of submissions || []) {
                if (s.submissionIdUuid) {
                    this.formattedDatesBySubmissionId.set(
                        s.submissionIdUuid,
                        S25Datefilter.transform(s.lastModDate, dateTimeFormat) || "",
                    );
                }
            }

            const allSubmissions = submissions || [];
            this.spawnedActionSubmissions = allSubmissions.filter((submission) => isWorkflowSpawnedDraft(submission));
            this.savedDraftSubmissions = allSubmissions.filter((submission) => isSelfSavedDraft(submission));
            this.submittedSubmissions = allSubmissions.filter(
                (submission) => submission.submitted && submission.workflowStatus !== "REQUESTED_CHANGES",
            );
            this.actionNeededSubmissions = allSubmissions.filter(
                (submission) => submission.workflowStatus === "REQUESTED_CHANGES",
            );

            const sortByLastModDesc = (first: IngestionSubmission, second: IngestionSubmission) =>
                new Date(second.lastModDate || 0).getTime() - new Date(first.lastModDate || 0).getTime();

            this.spawnedActionSubmissions.sort(sortByLastModDesc);
            this.savedDraftSubmissions.sort(sortByLastModDesc);
            this.submittedSubmissions.sort(sortByLastModDesc);
            this.actionNeededSubmissions.sort(sortByLastModDesc);

            this.rebuildIngestionPointFilterOptions();

            this.init = true;
            this.cd.detectChanges();
        } catch (error) {
            console.error("Failed to load dashboard:", error);
            S25Util.showError("Failed to load dashboard");
        }
    };

    startNewForm = (ingestionPointId: number) => {
        this.currentSubmissionIdUuid.set(null);
        this.currentIngestionPointId.set(ingestionPointId);
        this.isReadOnlyView.set(false);
        this.isEditMode.set(false);
        this.showDashboard.set(false);
    };

    continueForm = (submissionIdUuid: string) => {
        // Find the ingestion point ID from the submission
        try {
            const submission =
                this.spawnedActionSubmissions.find((row) => row.submissionIdUuid === submissionIdUuid) ||
                this.savedDraftSubmissions.find((row) => row.submissionIdUuid === submissionIdUuid) ||
                this.actionNeededSubmissions.find((row) => row.submissionIdUuid === submissionIdUuid);

            if (!submission) {
                S25Util.showError("Submission not found");
                return;
            }
            // Don't allow continuing submitted forms
            if (submission.submitted) {
                S25Util.showError(
                    "This form has already been submitted. Please view it from the Submitted Forms section.",
                );
                return;
            }
            this.currentSubmissionIdUuid.set(submissionIdUuid);
            this.currentIngestionPointId.set(submission.ingestionPointId!);
            this.isReadOnlyView.set(false);
            this.showDashboard.set(false);
        } catch (error) {
            console.error("Failed to continue form:", error);
            S25Util.showError("Failed to continue form");
        }
    };

    viewSubmission = async (submissionIdUuid: string) => {
        try {
            const submission =
                this.submittedSubmissions.find((row) => row.submissionIdUuid === submissionIdUuid) ||
                this.spawnedActionSubmissions.find((row) => row.submissionIdUuid === submissionIdUuid) ||
                this.savedDraftSubmissions.find((row) => row.submissionIdUuid === submissionIdUuid) ||
                this.actionNeededSubmissions.find((row) => row.submissionIdUuid === submissionIdUuid);
            if (!submission) {
                S25Util.showError("Submission not found");
                return;
            }
            this.currentSubmissionIdUuid.set(submissionIdUuid);
            this.currentIngestionPointId.set(submission.ingestionPointId!);
            this.isReadOnlyView.set(true);
            this.showDashboard.set(false);
        } catch (error) {
            console.error("Failed to view submission:", error);
            S25Util.showError("Failed to view submission");
        }
    };

    onForkContextCreated = (created: IngestionSubmission) => {
        if (!created.submissionIdUuid || created.ingestionPointId == null) {
            return;
        }
        this.currentSubmissionIdUuid.set(created.submissionIdUuid);
        this.currentIngestionPointId.set(created.ingestionPointId);
        this.isReadOnlyView.set(!!created.submitted);
        this.isEditMode.set(false);
        this.showDashboard.set(false);
    };

    editSubmission = async (submissionIdUuid: string) => {
        try {
            const submission = this.submittedSubmissions.find((s) => s.submissionIdUuid === submissionIdUuid);
            if (!submission) {
                S25Util.showError("Submission not found");
                return;
            }
            this.currentSubmissionIdUuid.set(submissionIdUuid);
            this.currentIngestionPointId.set(submission.ingestionPointId!);
            this.isReadOnlyView.set(false);
            this.isEditMode.set(true);
            this.showDashboard.set(false);
        } catch (error) {
            console.error("Failed to edit submission:", error);
            S25Util.showError("Failed to edit submission");
        }
    };

    onFormClose = () => {
        return this.returnToDashboard();
    };

    onFormSubmit = async (event: {
        submissionIdUuid: string;
        answers: IngestionFieldAnswer[];
        successMessage?: string | null;
    }) => {
        NotificationService.post(resolveSuccessMessagePlainText(event.successMessage), 10 * S25Const.ms.sec);
        await this.returnToDashboard();
    };

    private returnToDashboard = async () => {
        this.showDashboard.set(true);
        this.currentSubmissionIdUuid.set(null);
        this.currentIngestionPointId.set(null);
        this.isReadOnlyView.set(false);
        this.isEditMode.set(false);
        await this.refresh();
    };

    deleteSubmission = async (submissionIdUuid: string) => {
        if (!(await AlertService.confirm("Are you sure you want to delete this form? This action cannot be undone."))) {
            return;
        }
        try {
            await DataIngestionService.deleteSubmission(submissionIdUuid);
            await this.refresh();
        } catch (error) {
            console.error("Failed to delete form:", error);
            S25Util.showError(error);
        }
    };

    cancelWorkflow = async (submission: IngestionSubmission) => {
        if (
            !(await AlertService.confirm(
                "Are you sure you want to cancel this workflow? This action cannot be undone.",
            ))
        ) {
            return;
        }
        try {
            await WorkflowService.cancelWorkflow(submission.itemTypeId!, submission.itemId!);
            await this.refresh();
        } catch (error) {
            console.error("Error canceling workflow:", error);
            S25Util.showError(error);
        }
    };

    toggleDiagram = (submission: IngestionSubmission) => {
        this.diagramStore.showForInstance(submission.workflowProcessId, submission.workflowProcessInstanceId, {
            ...submission,
            name: submission.ingestionPoint?.ingestionPointName,
        });
    };

    openDiagramChat(request: DiagramChatRequest) {
        const submission = this.diagramStore.submission();
        if (!submission) return;
        void this.diagramChat.start(
            submission,
            { kind: "person", contId: request.contId, name: request.name, chatUser: request.chatUser },
            request.returnFocusTo,
        );
    }

    hasActiveFilters(): boolean {
        return (
            this.filterFormType() != null ||
            !!this.filterDateFrom()?.trim() ||
            !!this.filterDateTo()?.trim() ||
            !!this.filterStatus()?.trim()
        );
    }

    get filteredActionNeededSubmissions(): IngestionSubmission[] {
        return this.applyFilters(this.actionNeededSubmissions);
    }

    get filteredSpawnedActionSubmissions(): IngestionSubmission[] {
        return this.applyFilters(this.spawnedActionSubmissions);
    }

    get filteredSavedDraftSubmissions(): IngestionSubmission[] {
        return this.applyFilters(this.savedDraftSubmissions);
    }

    get filteredSubmittedSubmissions(): IngestionSubmission[] {
        return this.applyFilters(this.submittedSubmissions);
    }

    onFormTypeFilterChange(event: Event): void {
        const select = event.target as HTMLSelectElement;
        const rawValue = select.value;
        this.filterFormType.set(rawValue === "" ? null : Number(rawValue));
        this.cd.markForCheck();
    }

    onFilterDateFromChange(event: Event): void {
        const inputElement = event.target as HTMLInputElement;
        const rawValue = inputElement.value?.trim();
        this.filterDateFrom.set(rawValue || null);
        this.cd.markForCheck();
    }

    onFilterDateToChange(event: Event): void {
        const inputElement = event.target as HTMLInputElement;
        const rawValue = inputElement.value?.trim();
        this.filterDateTo.set(rawValue || null);
        this.cd.markForCheck();
    }

    onFilterStatusChange(event: Event): void {
        const select = event.target as HTMLSelectElement;
        const rawValue = select.value?.trim();
        this.filterStatus.set(rawValue || null);
        this.cd.markForCheck();
    }

    clearFilters = (): void => {
        this.filterFormType.set(null);
        this.filterDateFrom.set(null);
        this.filterDateTo.set(null);
        this.filterStatus.set(null);
        this.cd.markForCheck();
    };

    private rebuildIngestionPointFilterOptions(): void {
        const labelsByPointId = new Map<number, string>();
        for (const submission of [
            ...this.actionNeededSubmissions,
            ...this.spawnedActionSubmissions,
            ...this.savedDraftSubmissions,
            ...this.submittedSubmissions,
        ]) {
            if (submission.ingestionPointId == null) {
                continue;
            }
            const label =
                submission.ingestionPoint?.ingestionPointName?.trim() || `Form ${submission.ingestionPointId}`;
            labelsByPointId.set(submission.ingestionPointId, label);
        }
        this.ingestionPointFilterOptions = Array.from(labelsByPointId.entries())
            .map(([ingestionPointId, label]) => ({ ingestionPointId, label }))
            .sort((first, second) => first.label.localeCompare(second.label));
    }

    private applyFilters(submissions: IngestionSubmission[]): IngestionSubmission[] {
        let filtered = submissions || [];

        if (this.filterFormType() != null) {
            const formTypeId = this.filterFormType()!;
            filtered = filtered.filter((submission) => submission.ingestionPointId === formTypeId);
        }

        if (this.filterDateFrom()?.trim()) {
            const fromDate = S25DateParse.date(this.filterDateFrom()!);
            if (fromDate) {
                fromDate.setHours(0, 0, 0, 0);
                filtered = filtered.filter((submission) => {
                    if (!submission.lastModDate) {
                        return false;
                    }
                    const submissionDate = S25DateParse.date(submission.lastModDate);
                    if (!submissionDate) {
                        return false;
                    }
                    submissionDate.setHours(0, 0, 0, 0);
                    return submissionDate >= fromDate;
                });
            }
        }

        if (this.filterDateTo()?.trim()) {
            const toDate = S25DateParse.date(this.filterDateTo()!);
            if (toDate) {
                toDate.setHours(23, 59, 59, 999);
                filtered = filtered.filter((submission) => {
                    if (!submission.lastModDate) {
                        return false;
                    }
                    const submissionDate = S25DateParse.date(submission.lastModDate);
                    if (!submissionDate) {
                        return false;
                    }
                    return submissionDate <= toDate;
                });
            }
        }

        if (this.filterStatus()?.trim()) {
            const status = this.filterStatus()!;
            filtered = filtered.filter((submission) => submissionMatchesDashboardStatusFilter(submission, status));
        }

        return filtered;
    }

    readonly WORKFLOW_INGESTION_SUBMISSION_OBJECT_TYPE = Workflow.WORKFLOW_INGESTION_SUBMISSION_OBJECT_TYPE;
}
